/**
 * MentionIndicator Component
 * Visual badge/indicator shown on messages where the current user is mentioned
 * Displays with amber styling to stand out from regular messages
 */

import React from 'react';
import { AtSign } from 'lucide-react';

interface MentionIndicatorProps {
  /**
   * Display variant
   * - 'badge': Small badge with @ icon (for message list)
   * - 'inline': Inline indicator within message content
   */
  variant?: 'badge' | 'inline';

  /**
   * Optional className for custom styling
   */
  className?: string;

  /**
   * Whether this mention has been read
   */
  isRead?: boolean;
}

export const MentionIndicator: React.FC<MentionIndicatorProps> = ({
  variant = 'badge',
  className = '',
  isRead = false,
}) => {
  if (variant === 'inline') {
    return (
      <span
        className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${
          isRead
            ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300'
            : 'bg-amber-200 text-amber-900 dark:bg-amber-800/50 dark:text-amber-200'
        } ${className}`}
        title="You were mentioned"
      >
        <AtSign className="w-3 h-3" />
      </span>
    );
  }

  // Badge variant (default)
  return (
    <div
      className={`flex items-center justify-center w-6 h-6 rounded-full ${
        isRead
          ? 'bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400'
          : 'bg-amber-500 text-white dark:bg-amber-600 dark:text-white shadow-sm'
      } transition-colors ${className}`}
      title="You were mentioned in this message"
      aria-label="Mention indicator"
    >
      <AtSign className={`w-3.5 h-3.5 ${!isRead ? 'animate-pulse' : ''}`} />
    </div>
  );
};
